import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ScanLine, Loader2 } from "lucide-react";
import { listNoteSplitBatches } from "@/lib/noteClaim";
import { useClient } from "@/lib/clientContext";
import SplitBatchTracker from "@/components/claim/SplitBatchTracker";

const INK = "#141412", HAIR = "#E9E9EC", GREY = "#6E6E6B", SUB = "#6C6C69", AMBER = "#9A5A00";

const when = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return "";
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
};
const countsOf = (b) => ({
  created: b.created_count ?? (b.segments || []).filter((s) => s.status === "created").length,
  failed: b.failed_count ?? (b.segments || []).filter((s) => s.status !== "created").length,
});

// Part B: Multi-part uploads as a page in its own tab. One page, two panes — no stacked lightboxes.
// Left: Recent uploads. Right: the selected batch (reuses SplitBatchTracker's error queue + resolve + visits-created).
export default function MultiPartUploadsTab({ initialBatchId, backLabel = "Encounter review", onBack, onOpenVisit, onFind }) {
  const { selectedClientId: clientId } = useClient();
  const [batches, setBatches] = useState(null);
  const [sel, setSel] = useState(initialBatchId || null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      if (!clientId) { setBatches([]); return; }
      listNoteSplitBatches({ client_id: clientId, limit: 50 })
        .then((b) => alive && setBatches(b || []))
        .catch(() => alive && setBatches([]));
    };
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [clientId]);

  const sorted = useMemo(() => [...(batches || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [batches]);
  // Default selection: the batch we came for → newest with items to review → newest.
  useEffect(() => {
    if (!sorted.length) return;
    setSel((cur) => {
      if (cur && sorted.some((b) => b.id === cur)) return cur;
      if (initialBatchId && sorted.some((b) => b.id === initialBatchId)) return initialBatchId;
      return (sorted.find((b) => countsOf(b).failed > 0) || sorted[0]).id;
    });
  }, [sorted, initialBatchId]);

  const current = sorted.find((b) => b.id === sel) || null;

  return (
    <div className="px-7 py-6 max-w-[1180px] mx-auto" data-testid="mpu-tab">
      <button onClick={onBack} className="text-[12.5px] font-semibold inline-flex items-center gap-1 mb-2" style={{ color: "#5856D6" }} data-testid="mpu-tab-back"><ChevronLeft size={14} /> {backLabel}</button>
      <h1 className="text-[28px] font-bold leading-tight" style={{ color: INK }}>Multi-part uploads</h1>
      <p className="text-[13px] mt-1" style={{ color: SUB }}>One upload, many visits. revQ splits each file by patient and date of service.</p>

      <div className="mt-5 grid gap-4" style={{ gridTemplateColumns: "300px minmax(0,1fr)" }}>
        {/* Left — Recent uploads */}
        <aside className="self-start rounded-[12px] border bg-white p-1.5 sticky top-3" style={{ borderColor: HAIR }} data-testid="mpu-recent">
          <div className="flex items-center gap-2 px-2 py-2">
            <span className="text-[10.5px] font-extrabold uppercase tracking-[.06em]" style={{ color: GREY }}>Recent uploads</span>
            <span className="text-[12px]" style={{ color: GREY }}>{sorted.length}</span>
          </div>
          <div className="space-y-0.5 max-h-[calc(100vh-220px)] overflow-auto">
            {batches == null ? (
              <div className="text-[12.5px] py-6 text-center inline-flex w-full items-center justify-center gap-2" style={{ color: GREY }}><Loader2 size={13} className="animate-spin" />Loading…</div>
            ) : !sorted.length ? (
              <div className="text-[12.5px] py-8 text-center px-3" style={{ color: GREY }} data-testid="mpu-empty">No multi-part uploads yet. Use <b style={{ color: INK }}>Upload claims</b> to drop a bundle.</div>
            ) : sorted.map((b) => {
              const c = countsOf(b); const active = b.id === sel; const proc = b.status === "processing"; const failedState = b.status === "failed";
              return (
                <button key={b.id} onClick={() => setSel(b.id)} className="w-full text-left rounded-[10px] px-2.5 py-2 flex items-start gap-2" style={{ background: active ? "#F1F1F4" : "transparent" }} data-testid={`mpu-batch-${b.id}`}>
                  <ScanLine size={15} className="mt-[1px] flex-none" style={{ color: SUB }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold truncate" style={{ color: INK }}>{b.filename || "Upload"}</span>
                    <span className="block text-[12px] mt-0.5" style={{ color: GREY }}>
                      {proc ? <span className="inline-flex items-center gap-1.5" style={{ color: "#5856D6" }}><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#5856D6" }} />Splitting {b.num_pages || ""} pages…</span>
                        : failedState ? <span style={{ color: AMBER, fontWeight: 600 }}>Couldn't finish</span>
                        : c.failed > 0 ? <><span style={{ color: AMBER, fontWeight: 600 }}>{c.failed} to review</span> · {c.created} visit{c.created === 1 ? "" : "s"} created</>
                        : <>{c.created} visit{c.created === 1 ? "" : "s"} created</>}
                    </span>
                    {b.created_at ? <span className="block text-[11.5px] mt-0.5" style={{ color: "#9B9B97" }}>{when(b.created_at)}</span> : null}
                  </span>
                  {(c.failed > 0 || failedState) ? <span className="mt-1 w-2 h-2 rounded-full flex-none" style={{ background: "#E8A33D" }} data-testid={`mpu-batch-amber-${b.id}`} /> : null}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right — the selected batch (reuses the existing tracker: header, To review + resolve, Visits created) */}
        <section className="min-w-0" data-testid="mpu-selected">
          {!current ? (
            <div className="rounded-[12px] border bg-white py-16 text-center text-[13px]" style={{ borderColor: HAIR, color: SUB }}>Select an upload to see its visits.</div>
          ) : (
            <SplitBatchTracker key={current.id} batchId={current.id} clientId={clientId} onOpenVisit={onOpenVisit} onFind={onFind} embedded />
          )}
        </section>
      </div>
    </div>
  );
}
